// @ts-check
// Fuente de verdad única de los tres bloques del itinerario (diagrama ->
// pseudocódigo -> Python): su identificador, su sección de navegación y la
// página donde vive su editor.
//
// ## Por qué existe
//
// La lista `['diagrama', 'pseudocodigo', 'python']` estaba copiada en seis
// módulos y el mismo tipo declarado como `@typedef` independiente en cuatro,
// con el mapa bloque -> página repetido en otros cuatro. Añadir o renombrar un
// bloque obligaba a acertar en catorce sitios a la vez, y los olvidos NO
// fallaban igual en todos: `normalizarBloque` (`services/admin-client.js` y
// `services/my-attempts-client.js`) degrada a 'diagrama' cualquier valor que
// no esté en su lista, así que una lista desactualizada etiquetaría intentos
// del bloque nuevo como diagramas sin lanzar ni registrar nada. Con una sola
// lista ese modo de fallo silencioso deja de ser alcanzable por descuido.
//
// Vive en `utils/` por el mismo motivo ya documentado en `utils/etiquetas.js`
// (que resolvió este mismo problema para las etiquetas): son datos puros sin
// DOM que importan a la vez `services/**`, `state/**`, `components/**`,
// `pages/**` y `features/**`, y `utils` es la única capa que todas ellas
// pueden importar sin invertir ninguna flecha de docs/03-frontend.md §4.2.
// `state/` NO valía: `state/progress-dashboard.js` ya importa
// `services/progress-client.js`, así que declarar aquí lo que los servicios
// necesitan habría cerrado un ciclo entre carpetas.
//
// ## Qué NO vive aquí
//
// Los identificadores viajan al backend y los `href` son URLs reales: ningún
// valor de este módulo se puede cambiar sin romper el contrato o los enlaces.
//
// Las etiquetas de otro registro se quedan donde estaban a propósito, porque
// no son duplicados sino textos distintos para contextos distintos:
// `utils/etiquetas.js#ETIQUETA_BLOQUE` ('Diagrama', en singular, para filtros
// y tablas del panel de administración) y
// `features/translation/index.js#NOMBRE_REPRESENTACION` ('diagrama de flujo',
// en prosa, dentro de frases dirigidas al alumno). Unificarlas cambiaría
// texto visible en alguna pantalla.

/**
 * Identificador de bloque tal y como lo emite y espera el backend. No se
 * traduce ni se normaliza a otra forma en ningún punto del frontend.
 * @typedef {'diagrama' | 'pseudocodigo' | 'python'} Bloque
 */

/**
 * Sección de navegación correspondiente a cada bloque. NO coincide con el
 * identificador del bloque: la del diagrama es 'diagramas', en plural, porque
 * `components/top-nav.js` la fijó así antes de que existiera el panel de
 * progreso. Se conserva la discrepancia porque cambiarla alteraría el valor de
 * `aria-current` y el contrato público del componente.
 * @typedef {'diagramas' | 'pseudocodigo' | 'python'} SeccionBloque
 */

/**
 * @typedef {{
 *   id: Bloque,
 *   seccion: SeccionBloque,
 *   href: string,
 *   texto: string,
 *   textoNav: string,
 * }} InfoBloque
 */

/**
 * Los tres bloques con todo lo que los identifica, en orden PEDAGÓGICO (el del
 * itinerario del alumno), que es también el orden en que los recorre el panel
 * de progreso y en el que se pintan los enlaces de la barra superior.
 *
 * `texto` y `textoNav` son dos etiquetas distintas del mismo bloque y las dos
 * se conservan: solo difieren en 'diagrama' ('Diagramas' en la tarjeta del
 * panel de progreso, donde el contexto ya es "tu progreso por bloque", frente
 * a 'Editor de diagramas' en la barra de navegación, donde hace falta decir a
 * dónde lleva el enlace). Unificarlas cambiaría texto visible.
 * @type {ReadonlyArray<InfoBloque>}
 */
export const BLOQUES_INFO = /** @type {const} */ ([
  {
    id: 'diagrama',
    seccion: 'diagramas',
    href: 'index.html',
    texto: 'Diagramas',
    textoNav: 'Editor de diagramas',
  },
  {
    id: 'pseudocodigo',
    seccion: 'pseudocodigo',
    href: 'pseudocode.html',
    texto: 'Pseudocódigo',
    textoNav: 'Pseudocódigo',
  },
  {
    id: 'python',
    seccion: 'python',
    href: 'python.html',
    texto: 'Python',
    textoNav: 'Python',
  },
]);

/**
 * Los identificadores de bloque, derivados de `BLOQUES_INFO` para que no
 * puedan divergir de él.
 * @type {ReadonlyArray<Bloque>}
 */
export const BLOQUES = BLOQUES_INFO.map((info) => info.id);

/**
 * Mapa INVERSO página -> sección, derivado de `BLOQUES_INFO`. Existe para que
 * `pages/shell-nav.js#detectarSeccionActiva` no vuelva a escribirlo a mano:
 * era la cuarta copia del mismo mapa y la única en sentido contrario, así que
 * también era la que más fácilmente se quedaba atrás.
 * @type {ReadonlyMap<string, SeccionBloque>}
 */
export const SECCION_POR_PAGINA = new Map(BLOQUES_INFO.map((info) => [info.href, info.seccion]));

/**
 * Comprueba si un valor cualquiera es un identificador de bloque válido.
 * @param {unknown} valor
 * @returns {valor is Bloque}
 */
export function esBloque(valor) {
  return typeof valor === 'string' && BLOQUES.includes(/** @type {Bloque} */ (valor));
}

/**
 * Datos de un bloque, o `undefined` si el identificador no es de los tres.
 * Se devuelve `undefined` en vez de un objeto de respaldo para que cada
 * consumidor decida qué enseñar ante un valor desconocido (el panel de
 * progreso, por ejemplo, prefiere pintar el código crudo antes que un hueco).
 * @param {string} bloque
 * @returns {InfoBloque | undefined}
 */
export function infoDeBloque(bloque) {
  return BLOQUES_INFO.find((info) => info.id === bloque);
}
